import { createContext, ReactNode, useContext, useMemo } from 'react';
import { Checkpoint, Replay, sampleAt, Tick } from '../model/Replay';
import { Icon } from './Icons';

// ============ Sync context ============
export interface SyncCtxValue {
  scrubT: number;
  hoverT: number | null;
  setHoverT: (t: number | null) => void;
}

export const SyncContext = createContext<SyncCtxValue>({
  scrubT: 0.45,
  hoverT: null,
  setHoverT: () => {},
});

export function useChartSync(): SyncCtxValue {
  return useContext(SyncContext);
}

// ============ ChartFrame ============
export function ChartFrame({
  title,
  unit,
  height = 140,
  children,
  accent,
  value,
  valueColor,
  collapsed = false,
  onToggle,
  legend,
  hideHeader = false,
}: {
  title?: ReactNode;
  unit?: ReactNode;
  height?: number;
  children?: ReactNode;
  accent?: string;
  value?: ReactNode;
  valueColor?: string;
  collapsed?: boolean;
  onToggle?: () => void;
  legend?: ReactNode;
  hideHeader?: boolean;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        overflow: 'hidden',
      }}
    >
      {!hideHeader && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 14px',
            borderBottom: collapsed ? 'none' : '1px solid var(--border-subtle)',
            background: 'var(--bg-surface-2)',
          }}
        >
          <div style={{ width: 3, height: 14, background: accent || 'var(--accent)', borderRadius: 2 }} />
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-1)', letterSpacing: '0.01em' }}>{title}</div>
          {unit && <span style={{ fontSize: 11, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>{unit}</span>}
          <div style={{ flex: 1 }} />
          {legend}
          {value !== undefined && (
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                fontWeight: 600,
                color: valueColor || 'var(--fg-1)',
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.01em',
              }}
            >
              {value}
            </span>
          )}
          {onToggle && (
            <button
              onClick={onToggle}
              style={{
                width: 22,
                height: 22,
                borderRadius: 4,
                border: 'none',
                background: 'transparent',
                color: 'var(--fg-3)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: collapsed ? 'rotate(-90deg)' : 'none',
                transition: 'transform 0.15s',
              }}
            >
              <Icon.Caret />
            </button>
          )}
        </div>
      )}
      {!collapsed && <div style={{ position: 'relative', height }}>{children}</div>}
    </div>
  );
}

// ============ Helpers ============
function makePath(
  values: number[],
  height: number,
  padTop = 6,
  padBot = 6,
  min: number | null = null,
  max: number | null = null,
): string {
  if (!values || values.length === 0) return '';
  const mn = min !== null ? min : Math.min(...values);
  const mx = max !== null ? max : Math.max(...values);
  const range = mx - mn || 1;
  const h = height - padTop - padBot;
  return values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * 100;
      const y = padTop + (1 - (v - mn) / range) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

function sampleTicks<K extends keyof Tick>(ticks: Tick[], N: number, dim: K): number[] {
  if (ticks.length <= N) return ticks.map((tk) => Number(tk[dim]));
  const step = ticks.length / N;
  const out: number[] = [];
  for (let i = 0; i < N; i++) {
    out.push(Number(ticks[Math.floor(i * step)][dim]));
  }
  return out;
}

// ============ Crosshair overlay ============
export function CrosshairOverlay({
  checkpoints,
  duration,
  height,
  showHover = true,
}: {
  checkpoints?: Checkpoint[];
  duration: number;
  height: number;
  showHover?: boolean;
}) {
  const { scrubT, hoverT } = useChartSync();
  return (
    <g>
      {(checkpoints || []).map((cp, i) => {
        const x = (cp.t / duration) * 100;
        return (
          <line
            key={'cp-' + i}
            x1={`${x}%`}
            x2={`${x}%`}
            y1="0"
            y2={height}
            stroke="var(--fg-dim)"
            strokeWidth="1"
            strokeDasharray="2 3"
          />
        );
      })}
      {showHover && hoverT !== null && (
        <line
          x1={`${hoverT * 100}%`}
          x2={`${hoverT * 100}%`}
          y1="0"
          y2={height}
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="1"
        />
      )}
      {scrubT !== null && (
        <line
          x1={`${scrubT * 100}%`}
          x2={`${scrubT * 100}%`}
          y1="0"
          y2={height}
          stroke="#fff"
          strokeWidth="1.5"
          opacity="0.9"
        />
      )}
    </g>
  );
}

// ============ Speed ============
export function SpeedChart({
  replay,
  height = 140,
  kmh = true,
  showAvg = true,
  showTop = true,
}: {
  replay: Replay;
  height?: number;
  kmh?: boolean;
  showAvg?: boolean;
  showTop?: boolean;
}) {
  const { ticks, duration, topSpeed, avgSpeed, checkpoints } = replay.data;
  const N = 280;
  const data = useMemo(() => sampleTicks(ticks, N, 'speed'), [ticks]);
  const top = kmh ? topSpeed * 3.6 : topSpeed;
  const avg = kmh ? avgSpeed * 3.6 : avgSpeed;
  const max = top * 1.05;
  const path = makePath(kmh ? data.map((v) => v * 3.6) : data, height, 8, 8, 0, max);
  const areaPath = path + ` L 100 ${height - 8} L 0 ${height - 8} Z`;

  const { scrubT } = useChartSync();
  const cur = sampleAt(replay, scrubT);
  const curVal = kmh ? cur.speed * 3.6 : cur.speed;

  return (
    <ChartFrame title="Speed" accent="var(--accent)" height={height} unit={kmh ? 'km/h' : 'm/s'} value={curVal.toFixed(1)}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}
      >
        <defs>
          <linearGradient id={`spd-${replay.meta.replayUid}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="var(--accent)" stopOpacity="0.4" />
            <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#spd-${replay.meta.replayUid})`} />
        <path d={path} fill="none" stroke="var(--accent)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        {showAvg && (
          <line
            x1="0"
            x2="100"
            y1={8 + (1 - avg / max) * (height - 16)}
            y2={8 + (1 - avg / max) * (height - 16)}
            stroke="var(--fg-muted)"
            strokeWidth="0.8"
            strokeDasharray="3 3"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {showTop && (
          <line
            x1="0"
            x2="100"
            y1={8 + (1 - top / max) * (height - 16)}
            y2={8 + (1 - top / max) * (height - 16)}
            stroke="var(--bad)"
            strokeWidth="0.8"
            strokeDasharray="3 3"
            vectorEffect="non-scaling-stroke"
            opacity="0.7"
          />
        )}
        <CrosshairOverlay checkpoints={checkpoints} duration={duration} height={height} />
      </svg>
      <div
        style={{
          position: 'absolute',
          right: 8,
          top: 6,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          color: 'var(--fg-3)',
        }}
      >
        <div>
          <span style={{ color: 'var(--bad)' }}>━</span> top {top.toFixed(0)}
        </div>
        <div>
          <span style={{ color: 'var(--fg-muted)' }}>┄</span> avg {avg.toFixed(0)}
        </div>
      </div>
    </ChartFrame>
  );
}

// ============ Steering ============
export function SteerChart({ replay, height = 140 }: { replay: Replay; height?: number }) {
  const { ticks, duration, clusters, checkpoints } = replay.data;
  const N = 400;
  const data = useMemo(() => sampleTicks(ticks, N, 'steer'), [ticks]);
  const path = makePath(data, height, 8, 8, -1, 1);
  const midY = 8 + 0.5 * (height - 16);
  const { scrubT } = useChartSync();
  const cur = sampleAt(replay, scrubT);

  return (
    <ChartFrame
      title="Steering"
      accent={clusters.length > 4 ? 'var(--bad)' : 'var(--accent)'}
      height={height}
      unit="−1 ↔ +1"
      value={`${cur.steer >= 0 ? '+' : ''}${cur.steer.toFixed(3)}`}
      valueColor={Math.abs(cur.steer) > 0.05 ? (cur.steer > 0 ? '#ff8a3c' : '#0099ff') : 'var(--fg-1)'}
      legend={
        clusters.length > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 10,
              color: 'var(--bad)',
              fontFamily: 'var(--font-mono)',
              padding: '2px 6px',
              background: 'var(--bad-12)',
              borderRadius: 4,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 2, background: 'var(--bad)' }} />
            {clusters.length} suspicious cluster{clusters.length === 1 ? '' : 's'}
          </span>
        )
      }
    >
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}
      >
        {clusters.map((c, i) => {
          const x1 = (c.tStart / duration) * 100;
          const x2 = (c.tEnd / duration) * 100;
          return (
            <rect
              key={'cluster-' + i}
              x={`${x1}%`}
              y="0"
              width={`${Math.max(0.5, x2 - x1)}%`}
              height={height}
              fill="var(--bad)"
              fillOpacity="0.15"
            />
          );
        })}
        <line
          x1="0"
          x2="100"
          y1={midY}
          y2={midY}
          stroke="var(--fg-dim)"
          strokeWidth="1"
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
        <path d={path} fill="none" stroke={clusters.length > 4 ? 'var(--bad)' : '#fff'} strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
        <CrosshairOverlay checkpoints={checkpoints} duration={duration} height={height} />
      </svg>
      <div
        style={{
          position: 'absolute',
          left: 4,
          top: 4,
          bottom: 4,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          color: 'var(--fg-muted)',
        }}
      >
        <span>+1</span>
        <span>0</span>
        <span>−1</span>
      </div>
    </ChartFrame>
  );
}

// ============ Throttle/Brake ============
export function ThrottleBrakeChart({ replay, height = 90 }: { replay: Replay; height?: number }) {
  const { ticks, duration, checkpoints } = replay.data;
  const N = 400;
  const gas = useMemo(() => sampleTicks(ticks, N, 'gas').map((v) => (v ? 1 : 0)), [ticks]);
  const brake = useMemo(() => sampleTicks(ticks, N, 'brake').map((v) => (v ? 1 : 0)), [ticks]);

  function bars(arr: number[], color: string, yBase: number, h: number) {
    const segs: JSX.Element[] = [];
    let runStart: number | null = null;
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] && runStart === null) runStart = i;
      else if (!arr[i] && runStart !== null) {
        segs.push(
          <rect
            key={`${color}-${runStart}`}
            x={`${(runStart / arr.length) * 100}%`}
            y={yBase}
            width={`${((i - runStart) / arr.length) * 100}%`}
            height={h}
            fill={color}
          />,
        );
        runStart = null;
      }
    }
    if (runStart !== null) {
      segs.push(
        <rect
          key={`${color}-${runStart}`}
          x={`${(runStart / arr.length) * 100}%`}
          y={yBase}
          width={`${((arr.length - runStart) / arr.length) * 100}%`}
          height={h}
          fill={color}
        />,
      );
    }
    return segs;
  }

  const { scrubT } = useChartSync();
  const cur = sampleAt(replay, scrubT);

  return (
    <ChartFrame
      title="Throttle / Brake"
      accent="var(--ok)"
      height={height}
      unit="binary"
      value={cur.brake ? 'BRAKE' : cur.gas ? 'GAS' : '— —'}
      valueColor={cur.brake ? 'var(--bad)' : cur.gas ? 'var(--ok)' : 'var(--fg-muted)'}
    >
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}
      >
        {bars(gas, 'var(--ok)', 12, (height - 24) / 2 - 3)}
        {bars(brake, 'var(--bad)', 12 + (height - 24) / 2 + 3, (height - 24) / 2 - 3)}
        <CrosshairOverlay checkpoints={checkpoints} duration={duration} height={height} />
      </svg>
      <div
        style={{
          position: 'absolute',
          left: 6,
          top: 10,
          bottom: 6,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          color: 'var(--fg-muted)',
        }}
      >
        <span style={{ color: 'var(--ok)' }}>GAS</span>
        <span style={{ color: 'var(--bad)' }}>BRK</span>
      </div>
    </ChartFrame>
  );
}

// ============ Gear ============
export function GearChart({ replay, height = 110 }: { replay: Replay; height?: number }) {
  const { ticks, duration, checkpoints } = replay.data;
  const N = 400;
  const data = useMemo(() => sampleTicks(ticks, N, 'gear'), [ticks]);
  const stepPath = useMemo(() => {
    let p = '';
    for (let i = 0; i < data.length; i++) {
      const x = (i / (data.length - 1)) * 100;
      const y = 8 + (1 - data[i] / 5) * (height - 16);
      if (i === 0) p += `M${x.toFixed(2)} ${y.toFixed(2)}`;
      else {
        p += ` L${x.toFixed(2)} ${(8 + (1 - data[i - 1] / 5) * (height - 16)).toFixed(2)} L${x.toFixed(2)} ${y.toFixed(2)}`;
      }
    }
    return p;
  }, [data, height]);
  const { scrubT } = useChartSync();
  const cur = sampleAt(replay, scrubT);

  return (
    <ChartFrame title="Gear" accent="#ffe14a" height={height} unit="1–5" value={`G${cur.gear}`}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}
      >
        {[1, 2, 3, 4, 5].map((g) => (
          <line
            key={'gb-' + g}
            x1="0"
            x2="100"
            y1={8 + (1 - g / 5) * (height - 16)}
            y2={8 + (1 - g / 5) * (height - 16)}
            stroke="var(--fg-dim)"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <path d={stepPath} fill="none" stroke="#ffe14a" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        <CrosshairOverlay checkpoints={checkpoints} duration={duration} height={height} />
      </svg>
      <div
        style={{
          position: 'absolute',
          left: 4,
          top: 4,
          bottom: 4,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          color: 'var(--fg-muted)',
        }}
      >
        {[5, 4, 3, 2, 1].map((g) => (
          <span key={g}>G{g}</span>
        ))}
      </div>
    </ChartFrame>
  );
}

// ============ RPM ============
export function RPMChart({ replay, height = 110 }: { replay: Replay; height?: number }) {
  const { ticks, duration, checkpoints } = replay.data;
  const N = 280;
  const data = useMemo(() => sampleTicks(ticks, N, 'rpm'), [ticks]);
  const path = makePath(data, height, 8, 8, 2000, 8200);
  const { scrubT } = useChartSync();
  const cur = sampleAt(replay, scrubT);
  return (
    <ChartFrame title="RPM" accent="#ff8a3c" height={height} unit="rev/min" value={cur.rpm.toFixed(0)}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}
      >
        <rect x="0" y="8" width="100" height={(1 - 7500 / 8200) * (height - 16) - 8 + 16} fill="var(--bad)" fillOpacity="0.06" />
        <path d={path} fill="none" stroke="#ff8a3c" strokeWidth="1.3" vectorEffect="non-scaling-stroke" />
        <CrosshairOverlay checkpoints={checkpoints} duration={duration} height={height} />
      </svg>
    </ChartFrame>
  );
}

// ============ Pitch / Yaw / Roll ============
export function PYRChart({ replay, height = 110 }: { replay: Replay; height?: number }) {
  const { ticks, duration, checkpoints } = replay.data;
  const N = 280;
  const pitch = useMemo(() => sampleTicks(ticks, N, 'pitch'), [ticks]);
  const yaw = useMemo(() => sampleTicks(ticks, N, 'yaw'), [ticks]);
  const roll = useMemo(() => sampleTicks(ticks, N, 'roll'), [ticks]);
  const subH = (height - 4) / 3;
  return (
    <ChartFrame title="Pitch · Yaw · Roll" accent="#b56bff" height={height} unit="rad">
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}
      >
        {[
          { data: pitch, color: '#b56bff', label: 'P', y: 0 },
          { data: yaw, color: '#ff6b9d', label: 'Y', y: subH + 2 },
          { data: roll, color: '#00d4c4', label: 'R', y: subH * 2 + 4 },
        ].map((row, i) => {
          const min = Math.min(...row.data), max = Math.max(...row.data);
          const range = max - min || 1;
          const path = row.data
            .map((v, j) => {
              const x = (j / (row.data.length - 1)) * 100;
              const y = row.y + 4 + (1 - (v - min) / range) * (subH - 8);
              return `${j === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
            })
            .join(' ');
          return (
            <g key={i}>
              <line
                x1="0"
                x2="100"
                y1={row.y + subH / 2}
                y2={row.y + subH / 2}
                stroke="var(--fg-dim)"
                strokeWidth="0.5"
                strokeDasharray="2 3"
                vectorEffect="non-scaling-stroke"
              />
              <path d={path} fill="none" stroke={row.color} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
            </g>
          );
        })}
        <CrosshairOverlay checkpoints={checkpoints} duration={duration} height={height} />
      </svg>
      <div
        style={{
          position: 'absolute',
          left: 4,
          top: 0,
          height,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          color: 'var(--fg-muted)',
        }}
      >
        <span style={{ color: '#b56bff' }}>PITCH</span>
        <span style={{ color: '#ff6b9d' }}>YAW</span>
        <span style={{ color: '#00d4c4' }}>ROLL</span>
      </div>
    </ChartFrame>
  );
}

// ============ Altitude ============
export function AltitudeChart({ replay, height = 90 }: { replay: Replay; height?: number }) {
  const { ticks, duration, checkpoints } = replay.data;
  const N = 280;
  const data = useMemo(() => sampleTicks(ticks, N, 'y'), [ticks]);
  const mn = Math.min(...data), mx = Math.max(...data);
  const path = makePath(data, height, 8, 8, mn - 2, mx + 2);
  const areaPath = path + ` L 100 ${height - 8} L 0 ${height - 8} Z`;
  const { scrubT } = useChartSync();
  const cur = sampleAt(replay, scrubT);
  return (
    <ChartFrame title="Altitude" accent="#5fb5ff" height={height} unit="m" value={cur.y.toFixed(1)}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}
      >
        <defs>
          <linearGradient id={`alt-${replay.meta.replayUid}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#5fb5ff" stopOpacity="0.3" />
            <stop offset="1" stopColor="#5fb5ff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#alt-${replay.meta.replayUid})`} />
        <path d={path} fill="none" stroke="#5fb5ff" strokeWidth="1.3" vectorEffect="non-scaling-stroke" />
        <CrosshairOverlay checkpoints={checkpoints} duration={duration} height={height} />
      </svg>
    </ChartFrame>
  );
}

// ============ Velocity vs speed ============
export function VelVsSpeedChart({ replay, height = 110 }: { replay: Replay; height?: number }) {
  const { ticks, duration, checkpoints } = replay.data;
  const N = 280;
  const speed = useMemo(() => sampleTicks(ticks, N, 'speed'), [ticks]);
  const vmag = useMemo(() => sampleTicks(ticks, N, 'vmag'), [ticks]);
  const mx = Math.max(...speed, ...vmag) * 1.05;
  const p1 = makePath(speed, height, 8, 8, 0, mx);
  const p2 = makePath(vmag, height, 8, 8, 0, mx);
  return (
    <ChartFrame
      title="Velocity vs Speed"
      accent="#00d4c4"
      height={height}
      unit="m/s"
      legend={
        <span style={{ display: 'inline-flex', gap: 8, fontSize: 10, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>
          <span>
            <span style={{ color: 'var(--accent)' }}>━</span> speed
          </span>
          <span>
            <span style={{ color: '#00d4c4' }}>━</span> |vel|
          </span>
        </span>
      }
    >
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}
      >
        <path d={p1} fill="none" stroke="var(--accent)" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
        <path d={p2} fill="none" stroke="#00d4c4" strokeWidth="1.2" strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
        <CrosshairOverlay checkpoints={checkpoints} duration={duration} height={height} />
      </svg>
    </ChartFrame>
  );
}

// ============ Input histogram ============
export function InputHistogram({ replay, height = 230 }: { replay: Replay; height?: number }) {
  const { bins, exactBins } = replay.data;
  const max = Math.max(...bins);
  // The backend tags each exact-value bin with isSuspicious (non-vanilla
  // repeating values). Map those onto histogram bin indices so they render red.
  const susBinIdxs = new Set<number>();
  exactBins.forEach((b) => {
    if (b.isSuspicious) {
      susBinIdxs.add(Math.round((b.value + 1) * 20));
    }
  });

  return (
    <ChartFrame
      title="Steering input distribution"
      accent="var(--bad)"
      height={height}
      unit="exact-value frequency"
      legend={
        <span style={{ display: 'inline-flex', gap: 8, fontSize: 10, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>
          <span>
            <span style={{ color: 'var(--fg-2)' }}>▮</span> analog
          </span>
          <span>
            <span style={{ color: 'var(--bad)' }}>▮</span> snapped value
          </span>
        </span>
      }
    >
      <div style={{ position: 'absolute', inset: 0, padding: '16px 20px 22px 30px' }}>
        <svg width="100%" height="100%" viewBox="0 0 410 100" preserveAspectRatio="none">
          {[-1, -0.5, 0, 0.5, 1].map((tk) => {
            const x = (tk + 1) * 200;
            return (
              <g key={tk}>
                <line x1={x} y1="0" x2={x} y2="100" stroke="var(--fg-dim)" strokeWidth="0.3" strokeDasharray="2 3" />
              </g>
            );
          })}
          {bins.map((c, i) => {
            const x = i * 10;
            const h = (c / max) * 92;
            const isSus = susBinIdxs.has(i);
            return (
              <rect
                key={i}
                x={x + 1}
                y={100 - h}
                width={8}
                height={h}
                fill={isSus ? 'var(--bad)' : 'var(--fg-2)'}
                opacity={isSus ? 1 : 0.6}
                rx="1"
              />
            );
          })}
          {exactBins
            .slice(0, 3)
            .filter((b) => b.count >= 30 && Math.abs(b.value) > 0.05)
            .map((b, i) => {
              const x = (b.value + 1) * 200;
              return (
                <g key={i}>
                  <line x1={x} y1="0" x2={x} y2="100" stroke="var(--bad)" strokeWidth="0.5" strokeDasharray="2 2" />
                </g>
              );
            })}
        </svg>
        <div
          style={{
            position: 'absolute',
            bottom: 4,
            left: 30,
            right: 20,
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            color: 'var(--fg-muted)',
          }}
        >
          <span>−1.0</span>
          <span>−0.5</span>
          <span>0</span>
          <span>+0.5</span>
          <span>+1.0</span>
        </div>
        {exactBins
          .slice(0, 2)
          .filter((b) => b.count >= 30 && Math.abs(b.value) > 0.05)
          .map((b, i) => {
            const xPct = ((b.value + 1) / 2) * 100;
            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: `calc(${xPct}% - 50px)`,
                  top: 8 + i * 22,
                  fontSize: 10,
                  color: 'var(--bad)',
                  fontFamily: 'var(--font-mono)',
                  padding: '2px 6px',
                  background: 'var(--bg-page)',
                  border: '1px solid var(--bad)',
                  borderRadius: 3,
                  whiteSpace: 'nowrap',
                }}
              >
                {b.value > 0 ? '+' : ''}
                {b.value.toFixed(3)} · ×{b.count}
              </div>
            );
          })}
      </div>
    </ChartFrame>
  );
}

// ============ Overlaid (compare) ============
export function OverlaidChart({
  replays,
  colors,
  dim,
  label,
  unit,
  min = null,
  max = null,
  step = false,
  height = 120,
}: {
  replays: Replay[];
  colors: string[];
  dim: keyof Tick;
  label: string;
  unit: string;
  min?: number | null;
  max?: number | null;
  step?: boolean;
  height?: number;
}) {
  const N = 280;
  let gMin = min, gMax = max;
  if (gMin === null) gMin = Math.min(...replays.flatMap((r) => r.data.ticks.map((t) => Number(t[dim]))));
  if (gMax === null) gMax = Math.max(...replays.flatMap((r) => r.data.ticks.map((t) => Number(t[dim]))));
  return (
    <ChartFrame
      title={label}
      unit={unit}
      height={height}
      accent="var(--accent)"
      legend={
        <span style={{ display: 'inline-flex', gap: 8, fontSize: 10, fontFamily: 'var(--font-mono)' }}>
          {replays.map((r, i) => (
            <span key={r.meta.replayUid} style={{ color: colors[i] }}>
              ━ {r.meta.author}
            </span>
          ))}
        </span>
      }
    >
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}
      >
        {replays.map((r, ri) => {
          const stride = Math.max(1, Math.floor(r.data.ticks.length / N));
          const samples: number[] = [];
          for (let i = 0; i < r.data.ticks.length; i += stride) samples.push(Number(r.data.ticks[i][dim]));
          const path = samples
            .map((v, i) => {
              const x = (i / (samples.length - 1)) * 100;
              const y = 6 + (1 - (v - (gMin as number)) / ((gMax as number) - (gMin as number) || 1)) * (height - 12);
              return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
            })
            .join(' ');
          return (
            <path
              key={r.meta.replayUid}
              d={path}
              fill="none"
              stroke={colors[ri]}
              strokeWidth="1.3"
              vectorEffect="non-scaling-stroke"
              opacity="0.9"
            />
          );
        })}
        <CrosshairOverlay checkpoints={replays[0].data.checkpoints} duration={replays[0].data.duration} height={height} />
      </svg>
    </ChartFrame>
  );
}
